import { browserHeaders } from '../auth/browserObservation';
/**
 * Журнал ходьбы (`zhur1`, владелец 08.09.2026) — съём поведения на ОБЩЕМ слое.
 *
 * Панель пишет слепок работы всех пользователей: открытия экранов, нажатия,
 * тайминги. Мышь не трекается. События копятся в очереди и уезжают БАТЧАМИ;
 * потеря батча НЕ ломает работу — журнал важен, но не важнее человека, поэтому
 * каждая дорога здесь обёрнута «молчаливым» catch.
 *
 * ⛔ БЕЗ PII: в журнал уходит ШАБЛОН маршрута (`/clients/:id`), вид сущности и
 * её номер — ни имён, ни телефонов. Серверный гейт канонизирует ещё раз и
 * не верит клиенту на слово.
 *
 * ⛔ reveal здесь НЕ отправляется: раскрытие номера пишет СЕРВЕР в момент,
 * когда отдаёт номер (POST /browse/phone) — след нельзя потерять или подделать.
 */

const API_BASE =
  (import.meta.env.VITE_API_URL as string | undefined) || 'https://api.opsmind.ru/api/v1';

export interface HodbaEvent {
  kind: 'page_view' | 'click';
  route: string;
  action?: string;
  entity_type?: string;
  entity_id?: number;
  duration_ms?: number;
  active_ms?: number;
  filial_id?: number;
  occurred_at: string;
}

/** Очередь ограничена: если сеть умерла надолго, старое выбрасывается, а не растёт вечно. */
const MAX_QUEUE = 500;
/** Пачка уезжает раз в столько мс — или раньше, когда накопилось FLUSH_AT. */
const FLUSH_EVERY_MS = 10_000;
const FLUSH_AT = 25;

let queue: HodbaEvent[] = [];
let timer: number | null = null;
let vkljuchen = false;

/** Текущий экран — обновляет слой HodbaSloy; клики привязываются к нему. */
let tekushchiyRoute = '/';

export function nazvatTekushchiyRoute(route: string): void {
  tekushchiyRoute = route;
}

export function tekushchiy(): string {
  return tekushchiyRoute;
}

/**
 * Шаблон экрана из адреса: числовые сегменты → `:id`, query отрезан.
 * Сущность — первый сегмент и первый числовой сегмент за ним.
 */
export function kanonAdresa(pathname: string): {
  route: string;
  entity_type: string;
  entity_id: number;
} {
  const chistyy = pathname.split(/[?#]/)[0] || '/';
  if (/^\/(accept-invite|vhod-po-klyuchu|reset-password)(\/|$)/.test(chistyy)) return { route: '/public-auth', entity_type: '', entity_id: 0 };
  const segmenty = chistyy.split('/');

  let entityId = 0;
  const shablon = segmenty
    .map((s) => {
      if (/^\d+$/.test(s)) {
        if (entityId === 0) entityId = parseInt(s, 10);
        return ':id';
      }
      return s;
    })
    .join('/');

  const pervyy = segmenty.find((s) => s !== '' && !/^\d+$/.test(s)) ?? '';
  const entityKarta: Record<string, string> = {
    // Экраны, чья карточка — строка известной таблицы: детектор аномалии
    // считает «просмотры карточек» по entity_id, имена таблиц — как на сервере.
    clients: 'customers',
    zapis: 'bookings',
  };

  return {
    route: shablon.slice(0, 160),
    entity_type: entityId > 0 ? (entityKarta[pervyy] ?? pervyy.slice(0, 64)) : '',
    entity_id: entityId,
  };
}

function filial(): number | undefined {
  try {
    const raw = localStorage.getItem('opsmind_current_filial_id');
    if (!raw) return undefined;
    const parsed = JSON.parse(raw) as { state?: { currentFilialId?: unknown } };
    const id = parsed?.state?.currentFilialId;
    return typeof id === 'number' ? id : undefined;
  } catch {
    return undefined;
  }
}

function xsrf(): string | null {
  try {
    const m = document.cookie.match(/(?:^|;\s*)XSRF-TOKEN=([^;]*)/);
    return m ? decodeURIComponent(m[1]) : null;
  } catch {
    return null;
  }
}

export function track(e: Omit<HodbaEvent, 'occurred_at' | 'filial_id'>): void {
  if (!vkljuchen || ['/public-auth', '/login'].includes(e.route)) return;

  try {
    queue.push({ ...e, filial_id: filial(), occurred_at: new Date().toISOString() });

    if (queue.length > MAX_QUEUE) {
      queue = queue.slice(-MAX_QUEUE);
    }

    if (queue.length >= FLUSH_AT) {
      void flush();
    } else if (timer === null) {
      timer = window.setTimeout(() => void flush(), FLUSH_EVERY_MS);
    }
  } catch {
    // Журнал никогда не роняет панель.
  }
}

export async function flush(): Promise<void> {
  if (timer !== null) {
    window.clearTimeout(timer);
    timer = null;
  }

  if (queue.length === 0) return;

  const events = queue;
  queue = [];

  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json', ...browserHeaders() };
    const token = xsrf();
    if (token) headers['X-XSRF-TOKEN'] = token;

    // keepalive: пачка доезжает и при закрытии вкладки (sendBeacon не умеет
    // заголовки, а без XSRF сервер откажет — поэтому fetch).
    await fetch(`${API_BASE}/browse/batch`, {
      method: 'POST',
      credentials: 'include',
      keepalive: true,
      headers,
      body: JSON.stringify({ events }),
    });
  } catch {
    // Потеря батча — допустимый исход по решению владельца: работа дороже слепка.
  }
}

/** Глобальный слушатель нажатий: декларативная разметка data-act, капчей — все кнопки. */
function poymatKlik(e: MouseEvent): void {
  try {
    const target = e.target as Element | null;
    if (!target || typeof target.closest !== 'function') return;

    const razmechennyy = target.closest<HTMLElement>('[data-act]');

    if (razmechennyy) {
      const { entity_type, entity_id } = kanonAdresa(window.location.pathname);
      track({
        kind: 'click',
        route: tekushchiyRoute,
        action: razmechennyy.dataset.act || '_unmarked',
        entity_type: razmechennyy.dataset.actEntity ?? entity_type,
        entity_id: Number(razmechennyy.dataset.actId ?? entity_id) || 0,
      });
      return;
    }

    // Неразмеченная кнопка пишется БЕЗ текста (текст кнопки может нести имя
    // клиентки) — «щедро, но без PII»: тайминг активности есть, содержимое нет.
    if (target.closest('button, [role="button"], a[href]')) {
      const { entity_type, entity_id } = kanonAdresa(window.location.pathname);
      track({ kind: 'click', route: tekushchiyRoute, action: '_unmarked', entity_type, entity_id });
    }
  } catch {
    // Журнал никогда не роняет панель.
  }
}

let otvyazatKlik: (() => void) | null = null;

/** Включение съёма — один раз из общего слоя. Повторный вызов — по-прежнему один слушатель. */
export function vkljuchit(): void {
  if (vkljuchen) return;
  vkljuchen = true;

  document.addEventListener('click', poymatKlik, { capture: true });
  otvyazatKlik = () => document.removeEventListener('click', poymatKlik, { capture: true });

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') void flush();
  });
  window.addEventListener('pagehide', () => void flush());
}

/** Для тестов: выключить и очистить. */
export function sbrosDlyaTestov(): void {
  vkljuchen = false;
  queue = [];
  if (timer !== null) {
    window.clearTimeout(timer);
    timer = null;
  }
  otvyazatKlik?.();
  otvyazatKlik = null;
}

/** Для тестов: заглянуть в очередь. */
export function ochered(): readonly HodbaEvent[] {
  return queue;
}
